/**
 * `/quote/documents` — Document uploads (M-QUOTE.3.c).
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeleteQuoteDocument,
  useQuoteDocuments,
  useUploadQuoteDocument,
} from "@/hooks/use-quote";
import { formatTimestamp } from "@/lib/formatters";

const DOC_TYPES = ["other", "quote", "invoice", "proposal", "email"] as const;

export function QuoteDocumentsPage() {
  const list = useQuoteDocuments();
  const uploadMut = useUploadQuoteDocument();
  const deleteMut = useDeleteQuoteDocument();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<(typeof DOC_TYPES)[number]>("other");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    uploadMut.mutate(
      { file, documentType: docType },
      {
        onSuccess: () => {
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Documents</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Upload past quotes, proposals, and reference PDFs. Files are private and used to ground
          AI-drafted responses.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Upload document</CardTitle>
          <CardDescription>PDF, DOCX, TXT, or EML. Up to 25 MB.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,.docx,.txt,.eml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,message/rfc822"
              className="text-sm"
              required
            />
            <select
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              value={docType}
              onChange={(e) => setDocType(e.target.value as (typeof DOC_TYPES)[number])}
            >
              {DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <Button type="submit" disabled={uploadMut.isPending}>
              {uploadMut.isPending ? "Uploading…" : "Upload"}
            </Button>
            {uploadMut.error && (
              <span className="text-sm text-destructive">{uploadMut.error.message}</span>
            )}
          </form>
        </CardContent>
      </Card>

      {list.isLoading && <Skeleton className="h-16 w-full" />}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load documents: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No documents uploaded</CardTitle>
            <CardDescription>
              Drop a few representative quotes here so the AI can mirror your tone and pricing
              patterns.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {list.data.items.map((doc) => (
            <li key={doc.id}>
              <Card>
                <CardContent className="flex items-start justify-between gap-3 pt-6">
                  <Link
                    to={`/quote/documents/${doc.id}`}
                    className="min-w-0 flex-1 rounded outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`quote-doc-row-link-${doc.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{doc.file_name}</span>
                      <Badge variant="outline">{doc.document_type}</Badge>
                      <Badge variant={doc.status === "ready" ? "default" : "outline"}>
                        {doc.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {doc.file_size_bytes != null
                        ? `${Math.round(doc.file_size_bytes / 1024)} KB · `
                        : ""}
                      {formatTimestamp(doc.created_at)}
                    </div>
                    {doc.error_message && (
                      <div className="text-xs text-destructive">{doc.error_message}</div>
                    )}
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMut.mutate(doc.id)}
                    disabled={deleteMut.isPending}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
